import { useDebounce } from '@mm/hooks';
import { ConversationsSearchResultMatch } from '@mm/state/actions/LiveChatCRUDActions';
import {
  ConversationDirection,
  LivechatMessageModel,
  MessageKind,
} from '@mm/state/model/LivechatMessageModel';
import { useActor } from '@xstate/react';
import { merge, zipObject } from 'lodash';
import React, {
  createRef,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useContextSelector } from 'use-context-selector';
import Spinner from '../../../../../common/components/Spinner';
import { ContactContext, EmptySelectedContact } from '../../contactMachine';
import { ConversationContext } from '../../conversationMachine';
import { LiveChatContext } from '../../LiveChatContext';
import {
  ConversationSession,
  getConversationSessionsFromMessages,
  getFormattedDate,
} from '../../shared';
import { ChannelIcon } from '../ChannelIcon';
import { IncomingMessage } from './IncomingMessage/IncomingMessage';
import './Messages.pcss';
import { OutgoingMessage } from './OutgoingMessage/OutgoingMessage';

export type SessionLoadState = {
  match: boolean;
  loaded: boolean;
};

const useComponentStateAndHandlers = () => {
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );
  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const clearSearchResultsMessages = () =>
    lcMachineSend({
      type: 'LC_CONTEXT_UPDATE',
      payload: {
        searchResultsMessages: [],
      },
    });

  const setSearchResultMatches = (matches: ConversationsSearchResultMatch[]) =>
    lcMachineSend({
      type: 'LC_CONTEXT_UPDATE',
      payload: {
        searchResultMatches: matches,
      },
    });

  const getSearchResultSession = (
    remUserId: string | undefined,
    sessionId: number,
    addingOlder: boolean,
    reset?: boolean,
  ) =>
    lcMachineSend({
      type: 'GET_SEARCH_RESULT_SESSION',
      payload: {
        remUserId,
        sessionId,
        addingOlder,
        reset,
      },
    });

  const {
    remoteUserId,
    searchString,
    searchResultsMessages,
    searchResultMatches,
  } = lcMachineState.context;

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const { sessions } = selectedContact;

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const [hoverMessageKey, setHoverMessageKey] = useState<string | undefined>(
    undefined,
  );

  const searchResultSessionScrollRef = useRef<HTMLDivElement>(null);
  // prettier-ignore
  const [messagesBySession, setMessagesBySession] =
    useState<ConversationSession[]>([]);
  const [currentMatchIdx, setCurrentMatchIdx] = useState<number | undefined>(
    undefined,
  );
  // prettier-ignore
  const [allSessionsLoadState, setAllSessionsLoadState] =
    useState<Record<number, SessionLoadState>>({});

  // prettier-ignore
  const [previousSessionId, setPreviousSessionId]
    = useState<number|undefined>(undefined);
  const [nextSessionId, setNextSessionId] = useState<number | undefined>(
    undefined,
  );
  const searchResultRefs = useRef<RefObject<HTMLDivElement>[]>([]);
  const [isLoadingNextMatchSession, setIsLoadingNextMatchSession] =
    useState(false);

  const getNextSessionId = (currentMatchSessionId: number) => {
    // Note: numeric object keys are autoamtically sorted ascending
    const sortedSessionsIds = Object.keys(allSessionsLoadState).map(k =>
      Number(k),
    );

    const indexInSorted = sortedSessionsIds.indexOf(currentMatchSessionId);
    return sortedSessionsIds
      .slice(indexInSorted + 1)
      .find(id => !allSessionsLoadState[id].loaded);
  };

  const getPreviousSessionId = (nextMatchSessionId: number) => {
    // Note: numeric object keys are automatically sorted ascending
    const sortedSessionsIds = Object.keys(allSessionsLoadState).map(k =>
      Number(k),
    );
    const indexInSorted = sortedSessionsIds.indexOf(nextMatchSessionId);
    return sortedSessionsIds
      .slice(0, indexInSorted)
      .reverse()
      .find(id => !allSessionsLoadState[id].loaded);
  };

  useEffect(() => {
    const sortedSessionsIds = Object.keys(allSessionsLoadState).map(k =>
      Number(k),
    );
    if (searchResultMatches.length && currentMatchIdx === undefined) {
      setCurrentMatchIdx(0);
      if (sortedSessionsIds.length > 1) {
        setPreviousSessionId(sortedSessionsIds[sortedSessionsIds.length - 2]);
      }
    }
    if (
      searchResultMatches.length &&
      sortedSessionsIds.length > 1 &&
      currentMatchIdx !== undefined
    ) {
      const indexInSorted = sortedSessionsIds.indexOf(
        searchResultMatches[currentMatchIdx]?.conversationSessionId,
      );
      if (indexInSorted > -1) {
        const prevSessionId = getPreviousSessionId(
          sortedSessionsIds[indexInSorted],
        );
        setPreviousSessionId(prevSessionId);
      }
      if (
        (indexInSorted > -1 && indexInSorted) <
        sortedSessionsIds.length - 1
      ) {
        const nextSessId = getNextSessionId(sortedSessionsIds[indexInSorted]);
        setNextSessionId(nextSessId);
      }
    }
  }, [searchResultMatches, allSessionsLoadState]);

  useEffect(() => {
    if (
      searchResultsMessages &&
      searchResultsMessages[0] &&
      searchResultsMessages[0].remoteUserId !== remoteUserId
    ) {
      searchResultRefs.current = [];
      setMessagesBySession([]);
      clearSearchResultsMessages();
      setSearchResultMatches([]);
      setAllSessionsLoadState({});
      setCurrentMatchIdx(0);
      setPreviousSessionId(undefined);
      setNextSessionId(undefined);
    }
  }, [remoteUserId]);

  useEffect(() => {
    const sessionsIds = sessions.map(s => s.id);

    const newSessionsValues = Array.from(
      { length: sessionsIds.length },
      () => ({ loaded: false, match: false }),
    );

    // Note: This guards the component from showing data belonging to a different user.
    // It is hard to accomplish same behavior in the subscriptions part.
    const uniqueNewMessagesSessionIds = [
      ...new Set(searchResultsMessages.map(m => m.conversationSessionId)),
    ];

    const newConversationsSessionsHash = zipObject(
      uniqueNewMessagesSessionIds,
      Array.from({ length: uniqueNewMessagesSessionIds.length }, () => ({
        loaded: true,
      })),
    );

    const mergedConversationsSessionsHash = merge(
      allSessionsLoadState,
      newConversationsSessionsHash,
    );

    const newAllSessionsLoadState = merge(
      zipObject(sessionsIds, newSessionsValues),
      mergedConversationsSessionsHash,
    );

    setAllSessionsLoadState(newAllSessionsLoadState);

    const messagesWithoutNotificatons = searchResultsMessages.filter(
      m =>
        ![MessageKind.read, MessageKind.delivery, MessageKind.fail].some(
          k => m.kind === k,
        ),
    );

    const conversations = getConversationSessionsFromMessages(
      messagesWithoutNotificatons,
      [],
      false,
      false,
    );
    setMessagesBySession(conversations);
  }, [
    /* Note: have to combine these seemingly unrelated items,
       since both need to update setAllSessionsLoadState.
       This leads to state update race conditions. Thus, have to combine. */
    sessions,
    searchResultsMessages,
  ]);

  useEffect(() => {
    if (searchResultMatches.length) {
      const refsArray = Array.from({ length: searchResultMatches.length }, () =>
        createRef<HTMLDivElement>(),
      );
      searchResultRefs.current = refsArray;
    }
  }, [searchResultMatches]);

  useEffect(() => {
    if (currentMatchIdx !== undefined) {
      const currentMatchRef = searchResultRefs.current[currentMatchIdx];
      if (currentMatchRef && currentMatchRef.current) {
        currentMatchRef.current.scrollIntoView(false);
      }
    }
  }, [
    currentMatchIdx,
    searchResultRefs.current[0] && searchResultRefs.current[0].current,
  ]);

  const onLoadOlderClick = async () => {
    if (previousSessionId) {
      await getSearchResultSession(remoteUserId, previousSessionId, true);

      selectedContactMachine?.send({
        type: 'LC_CONTACT_UPDATE',
        payload: {
          selectedSessionId: previousSessionId,
        },
      });
      const prevSessId = getPreviousSessionId(previousSessionId);
      setPreviousSessionId(prevSessId);
      if (searchResultSessionScrollRef.current) {
        searchResultSessionScrollRef.current.scrollIntoView(false);
      }
    }
  };

  const onLoadNewerClick = async () => {
    if (nextSessionId) {
      await getSearchResultSession(remoteUserId, nextSessionId, false);

      selectedContactMachine?.send({
        type: 'LC_CONTACT_UPDATE',
        payload: {
          selectedSessionId: nextSessionId,
        },
      });
      const nextSessId = getNextSessionId(nextSessionId);
      setNextSessionId(nextSessId);
    }
  };

  const onJumpToRecentClick = () => {
    clearSearchResultsMessages();

    selectedContactMachine?.send({
      type: 'LC_CONTACT_UPDATE',
      payload: {
        selectedSessionId: undefined,
      },
    });
    setPreviousSessionId(undefined);
    setNextSessionId(undefined);
  };

  const onNextMatch = async () => {
    if (
      currentMatchIdx !== undefined &&
      currentMatchIdx < searchResultMatches.length - 1
    ) {
      const nextMacth = searchResultMatches[currentMatchIdx + 1];
      const currentMatch = searchResultMatches[currentMatchIdx];
      if (
        currentMatch.conversationSessionId !==
          nextMacth.conversationSessionId &&
        allSessionsLoadState[nextMacth.conversationSessionId] &&
        !allSessionsLoadState[nextMacth.conversationSessionId].loaded
      ) {
        setIsLoadingNextMatchSession(true);
        await getSearchResultSession(
          remoteUserId,
          nextMacth.conversationSessionId,
          true,
        );
        setIsLoadingNextMatchSession(false);
        const prevSessionId = getPreviousSessionId(
          nextMacth.conversationSessionId,
        );
        const nSessionId = getNextSessionId(nextMacth.conversationSessionId);
        setPreviousSessionId(prevSessionId);
        setNextSessionId(nSessionId);

        selectedContactMachine?.send({
          type: 'LC_CONTACT_UPDATE',
          payload: {
            selectedSessionId: nextMacth.conversationSessionId,
          },
        });
      }
      setCurrentMatchIdx(currentMatchIdx + 1);
    }
  };

  const onPreviousMatch = async () => {
    if (currentMatchIdx && currentMatchIdx > 0) {
      const currentMatch = searchResultMatches[currentMatchIdx];
      if (currentMatch) {
        const nSessionId = getNextSessionId(currentMatch.conversationSessionId);
        setNextSessionId(nSessionId);

        selectedContactMachine?.send({
          type: 'LC_CONTACT_UPDATE',
          payload: {
            selectedSessionId: nSessionId,
          },
        });
      }
      setCurrentMatchIdx(currentMatchIdx - 1);
    }
  };

  return {
    currentMatchIdx,
    isLoadingNextMatchSession,
    hoverMessageKey,
    messagesBySession,
    onJumpToRecentClick,
    onLoadOlderClick,
    onLoadNewerClick,
    onNextMatch,
    onPreviousMatch,
    previousSessionId,
    nextSessionId,
    setHoverMessageKey,
    searchResultMatches,
    searchResultRefs,
    searchResultSessionScrollRef,
    searchString,
    remoteUserId,
    selectedContact,
  };
};

export const MessageSearchMatches = () => {
  const {
    previousSessionId,
    nextSessionId,
    currentMatchIdx,
    isLoadingNextMatchSession,
    hoverMessageKey,
    messagesBySession,
    onJumpToRecentClick,
    onLoadOlderClick,
    onLoadNewerClick,
    onNextMatch,
    onPreviousMatch,
    setHoverMessageKey,
    searchResultMatches,
    searchResultRefs,
    searchResultSessionScrollRef,
    searchString,
    remoteUserId,
    selectedContact,
  } = useComponentStateAndHandlers();

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState] = useActor(lcService);

  const isFetchingSearchResultsMessages = lcMachineState.matches({
    mainLogic: {
      active: {
        searchResultsMessagesFetching: 'fetching',
      },
    },
  });

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const refreshSelectedContact = useCallback(() => {
    selectedContactMachine?.send({
      type: 'LC_CONTACT_REFRESH',
      payload: remoteUserId || '',
    });
  }, [selectedContactMachine?.id, remoteUserId]);

  const conversationMachine = remoteUserId
    ? lcMachineState.context.conversationsRefs[remoteUserId]
    : undefined;

  const messagesChangingFast =
    (conversationMachine?.state.context as ConversationContext)?.allMessages ||
    [];

  const messages = useDebounce(messagesChangingFast, 47);

  const hasNext =
    currentMatchIdx !== undefined &&
    currentMatchIdx < searchResultMatches.length - 1;
  const hasPrevious = currentMatchIdx !== undefined && currentMatchIdx > 0;
  const latestMessageRead = messages.find(m => m.kind === 102);
  const latestIncomingMsg = messages.find(
    m => m.direction === 1 && m.kind !== 103,
  );

  const getMatchHighlightClassName = (m: LivechatMessageModel) => {
    const baseClass =
      m.direction === ConversationDirection.outgoing
        ? 'live-chat--message-search__match'
        : 'live-chat--message-search__match-bot';

    const idx =
      currentMatchIdx !== undefined
        ? currentMatchIdx
        : searchResultMatches.length;

    const matchedResult = searchResultMatches[idx];

    const ending =
      matchedResult && Number(m.time) === matchedResult.time ? '--active' : '';

    return baseClass + ending;
  };

  const maybeGetRef = (m: LivechatMessageModel) => {
    const idx =
      currentMatchIdx !== undefined
        ? currentMatchIdx
        : searchResultMatches.length;

    const matchedResult = searchResultMatches[idx];

    return matchedResult && Number(m.time) === matchedResult.time
      ? searchResultRefs.current[idx]
      : undefined;
  };

  const renderSearchResultsNavigation = () => (
    <div className="live-chat--messages__search-results">
      <div
        className={
          hasPrevious
            ? 'live-chat--messages__search-results--chevron'
            : 'live-chat--messages__search-results--chevron-disabled'
        }
        onClick={() => hasPrevious && onPreviousMatch()}
      >
        <i className="fas fa-chevron-down" />
      </div>
      <div
        className={
          hasNext
            ? 'live-chat--messages__search-results--chevron'
            : 'live-chat--messages__search-results--chevron-disabled'
        }
        onClick={() => hasNext && !isLoadingNextMatchSession && onNextMatch()}
      >
        <i className="fas fa-chevron-up" />
      </div>
      <div className="live-chat--messages__search-results--text">
        {searchResultMatches.length
          ? `
            ${currentMatchIdx !== undefined ? currentMatchIdx + 1 : ''}
              of ${searchResultMatches.length} results
              for "${searchString}"
            `
          : ''}
      </div>
      <div
        className="live-chat--messages__close--search-results"
        onClick={onJumpToRecentClick}
      >
        <i className="fas fa-times-circle" />
      </div>
    </div>
  );

  if (!selectedContact) return null;
  const { profilePic, fullName, variables, id: contactId } = selectedContact;
  // Note: Please note that the order of elements is reversed via CSS
  return (
    <div className="live-chat--search-results__wrapper">
      {renderSearchResultsNavigation()}
      <div className="live-chat--search-messages">
        <div ref={searchResultSessionScrollRef} />
        {previousSessionId && (
          <div className="live-chat--archived-messages__load--spacer">
            <div
              className="live-chat--archived-messages__load-more"
              onClick={onLoadOlderClick}
            >
              Load older messages...
            </div>
          </div>
        )}
        <Spinner
          show={isFetchingSearchResultsMessages}
          size={50}
          position="relative"
          backgroundColor="transparent"
        />

        {messagesBySession &&
          messagesBySession.map((c, idx) => (
            <div key={idx} className="live-chat--messages-conversation">
              <div className="live-chat--messages-conversation__time">
                <div className="live-chat--messages-conversation__time-hr" />
                <div className="live-chat--messages-conversation__time-text">
                  <div className="live-chat--messages-conversation__time-icon">
                    <ChannelIcon remoteUserId={remoteUserId} />
                  </div>

                  {getFormattedDate(c.sessionFirstMessageAt)}
                </div>
                <div className="live-chat--messages-conversation__time-hr" />
              </div>
              {c.sessionMessages &&
                (c.sessionMessages as LivechatMessageModel[]).map((m, i) => {
                  const messageKey = `${idx}-${i}`;
                  const isFirstMessage = i === c.sessionMessages.length - 1;

                  const read =
                    !!m.readAt ||
                    m.botId === '0' || // imported messages
                    (latestMessageRead && m.time < latestMessageRead.time) ||
                    (latestIncomingMsg && m.time < latestIncomingMsg.time);

                  return m.direction === ConversationDirection.incoming ? (
                    <IncomingMessage
                      messages={messages}
                      message={m}
                      isFirstMessage={isFirstMessage}
                      messageKey={messageKey}
                      hoverMessageKey={hoverMessageKey}
                      setHoverMessageKey={setHoverMessageKey}
                      profilePic={profilePic}
                      fullName={fullName}
                      isSearch
                      maybeGetRef={maybeGetRef}
                      getMatchHighlightClassName={getMatchHighlightClassName}
                      searchString={searchString}
                      variables={variables}
                      contactId={contactId}
                      refreshSelectedContact={refreshSelectedContact}
                    />
                  ) : (
                    <OutgoingMessage
                      messages={messages}
                      message={m}
                      isFirstMessage={isFirstMessage}
                      messageKey={messageKey}
                      profilePic={profilePic}
                      isSearch
                      maybeGetRef={maybeGetRef}
                      getMatchHighlightClassName={getMatchHighlightClassName}
                      searchString={searchString}
                      isFinalMessage={
                        idx === 0 && i === 0 && nextSessionId === undefined
                      }
                      read={read}
                    />
                  );
                })}
            </div>
          ))}

        {nextSessionId && (
          <div className="live-chat--archived-messages__load--spacer">
            <div
              className="live-chat--archived-messages__load-more"
              onClick={onLoadNewerClick}
            >
              Load newer messages...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
